import axios, {
	type AxiosError,
	type AxiosInstance,
	type AxiosResponse,
	type InternalAxiosRequestConfig,
} from "axios";

// Extend InternalAxiosRequestConfig to include metadata
interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
	metadata?: {
		startTime?: number;
	};
}
import {
	API_BASE_URL,
	API_ENDPOINTS,
	ERROR_CODES,
	RETRY_CONFIG,
} from "./constants";
import type { ApiResponse } from "@/types/api";
import { SecureTokenManager, JWTUtils } from "./secure-token-manager";
import { logger } from "./logger";

// Custom error class for API errors
export class ApiClientError extends Error {
	constructor(
		public code: number,
		message: string,
		public details?: Record<string, unknown>,
	) {
		super(message);
		this.name = "ApiClientError";
	}
}

// DEPRECATED: Use SecureTokenManager instead
export const TokenManager = {
	// Legacy method for backward compatibility
	get: (): string | null => {
		// In secure implementation, tokens should be in httpOnly cookies
		// This method should return null and rely on cookies
		console.warn(
			"TokenManager.get() is deprecated. Use SecureTokenManager for secure authentication.",
		);
		return null;
	},

	set: (_token: string): void => {
		console.warn(
			"TokenManager.set() is deprecated. Tokens should be set via httpOnly cookies by the server.",
		);
	},

	remove: (): void => {
		console.warn(
			"TokenManager.remove() is deprecated. Use SecureTokenManager.clearAuth().",
		);
		SecureTokenManager.clearAuth();
	},

	isExpired: JWTUtils.isExpired,
	shouldRefresh: JWTUtils.shouldRefresh,
};

// Secure token validation utilities
export const TokenValidator = {
	// Validate token without manual parsing
	validateToken: async (token: string): Promise<boolean> => {
		try {
			// Call backend to validate token instead of client-side validation
			const response = await axios.post(
				`${API_BASE_URL}/api/v1/auth/validate`,
				{},
				{
					headers: { Authorization: `Bearer ${token}` },
				},
			);
			return response.data.success;
		} catch {
			return false;
		}
	},

	// Check if user is authenticated via httpOnly cookies
	isAuthenticated: (): boolean => {
		return SecureTokenManager.isAuthenticated();
	},

	// Refresh token via secure backend endpoint
	refreshToken: async (): Promise<string | null> => {
		try {
			const response = await axios.post(
				`${API_BASE_URL}${API_ENDPOINTS.REFRESH}`,
				{},
				{
					withCredentials: false, // Important for httpOnly cookies
					headers: {
						"X-CSRF-Token": SecureTokenManager.getCSRFToken() || "",
					},
				},
			);

			if (response.data.success && response.data.data) {
				const data = response.data.data as {
					token?: string;
					csrfToken?: string;
				};
				// Store new JWT token for WebSocket
				if (data.token) {
					SecureTokenManager.setToken(data.token);
				}
				// Store new CSRF token if providedå
				if (data.csrfToken) {
					SecureTokenManager.setCSRFToken(data.csrfToken);
				}
				return data.token || null;
			}
			return null;
		} catch (error) {
			console.error("Secure token refresh failed:", error);
			return null;
		}
	},
};

// Create axios instance with secure defaults
const apiClient: AxiosInstance = axios.create({
	baseURL: API_BASE_URL,
	timeout: 30000, // 30 seconds
	withCredentials: false, // Enable httpOnly cookie support
	headers: {
		"Content-Type": "application/json",
		"X-Requested-With": "XMLHttpRequest", // CSRF protection
	},
});

// Request interceptor - Add JWT token and CSRF protection
apiClient.interceptors.request.use(
	async (config: ExtendedAxiosRequestConfig) => {
		// Add JWT token to Authorization header if available
		const token = SecureTokenManager.getToken();
		if (token) {
			config.headers.Authorization = `Bearer ${token}`;
		}

		// Add CSRF token for state-changing requests
		const csrfToken = SecureTokenManager.getCSRFToken();
		if (
			csrfToken &&
			["post", "put", "patch", "delete"].includes(
				config.method?.toLowerCase() || "",
			)
		) {
			config.headers["X-CSRF-Token"] = csrfToken;
		}

		// Log API requests
		const startTime = Date.now();
		logger.debug(`API Request: ${config.method?.toUpperCase()} ${config.url}`, {
			params: config.params,
			hasData: !!config.data,
			hasAuth: !!token,
		});

		// Store start time for duration tracking
		config.metadata = { startTime };

		return config;
	},
	(error) => {
		return Promise.reject(error);
	},
);

// Response interceptor - Handle errors securely
apiClient.interceptors.response.use(
	(response: AxiosResponse) => {
		// Store CSRF token if provided in response headers
		const csrfToken = response.headers["x-csrf-token"];
		if (csrfToken) {
			SecureTokenManager.setCSRFToken(csrfToken);
		}

		// Calculate request duration
		const startTime = (response.config as any).metadata?.startTime;
		const duration = startTime ? Date.now() - startTime : undefined;

		// Log API responses
		logger.trackApiCall(
			response.config.method?.toUpperCase() || "UNKNOWN",
			response.config.url || "UNKNOWN",
			response.status,
			duration,
			{
				success: response.data?.success,
				hasData: !!response.data?.data,
			},
		);

		return response;
	},
	async (error: AxiosError<ApiResponse>) => {
		const originalRequest = error.config as ExtendedAxiosRequestConfig & {
			_retry?: boolean;
		};

		if (!originalRequest) {
			return Promise.reject(error);
		}

		// Calculate request duration for errors
		const startTime = (originalRequest as any).metadata?.startTime;
		const duration = startTime ? Date.now() - startTime : undefined;

		// Log API errors
		logger.error(
			`API Error: ${originalRequest.method?.toUpperCase()} ${originalRequest.url}`,
			error,
			{
				status: error.response?.status,
				errorCode: (error.response?.data as any)?.error?.code,
				hasAuth: !!SecureTokenManager.isAuthenticated(),
				duration,
			},
		);

		// Handle authentication errors with secure redirect
		if (
			error.response?.status === 401 || // Unauthorized
			error.response?.data?.error?.code === ERROR_CODES.INVALID_TOKEN ||
			error.response?.data?.error?.code === ERROR_CODES.INVALID_CREDENTIALS ||
			error.response?.data?.error?.code === ERROR_CODES.ACCOUNT_DISABLED ||
			error.response?.data?.error?.code === ERROR_CODES.TOKEN_EXPIRED
		) {
			// Clear all auth data securely
			SecureTokenManager.clearAuth();

			// Redirect to login if not already there
			if (window.location.pathname !== "/login") {
				window.location.href = "/login";
			}
		}

		// Handle CSRF errors
		if (
			error.response?.status === 403 &&
			error.response?.data?.error?.message?.includes("CSRF")
		) {
			console.error("CSRF token validation failed");
			SecureTokenManager.clearAuth();
			if (window.location.pathname !== "/login") {
				window.location.href = "/login";
			}
		}

		// Convert API error to custom error
		const apiError = error.response?.data?.error;
		if (apiError) {
			throw new ApiClientError(
				apiError.code,
				apiError.message,
				apiError.details,
			);
		}

		// Handle network errors
		if (!error.response) {
			throw new ApiClientError(
				ERROR_CODES.INTERNAL_ERROR,
				"Network error. Please check your connection.",
			);
		}

		return Promise.reject(error);
	},
);

// Retry logic with exponential backoff
const retryRequest = async <T>(
	requestFn: () => Promise<T>,
	retries: number = RETRY_CONFIG.MAX_RETRIES,
): Promise<T> => {
	try {
		return await requestFn();
	} catch (error) {
		if (retries === 0) {
			throw error;
		}

		// Only retry on network errors or 5xx errors
		if (
			error instanceof ApiClientError &&
			error.code >= 1000 &&
			error.code < 2000
		) {
			throw error; // Don't retry client errors
		}

		const delay =
			RETRY_CONFIG.RETRY_DELAY *
			RETRY_CONFIG.RETRY_MULTIPLIER ** (RETRY_CONFIG.MAX_RETRIES - retries);

		await new Promise((resolve) => setTimeout(resolve, delay));

		return retryRequest(requestFn, retries - 1);
	}
};

// API client wrapper with retry logic
export const api = {
	get: async <T>(url: string, config?: Parameters<typeof apiClient.get>[1]) => {
		return retryRequest(() => apiClient.get<ApiResponse<T>>(url, config));
	},

	post: async <T>(
		url: string,
		data?: unknown,
		config?: Parameters<typeof apiClient.post>[2],
	) => {
		return retryRequest(() =>
			apiClient.post<ApiResponse<T>>(url, data, config),
		);
	},

	put: async <T>(
		url: string,
		data?: unknown,
		config?: Parameters<typeof apiClient.put>[2],
	) => {
		return retryRequest(() => apiClient.put<ApiResponse<T>>(url, data, config));
	},

	patch: async <T>(
		url: string,
		data?: unknown,
		config?: Parameters<typeof apiClient.patch>[2],
	) => {
		return retryRequest(() =>
			apiClient.patch<ApiResponse<T>>(url, data, config),
		);
	},

	delete: async <T>(
		url: string,
		config?: Parameters<typeof apiClient.delete>[1],
	) => {
		return retryRequest(() => apiClient.delete<ApiResponse<T>>(url, config));
	},
};

// Export the raw axios instance for special cases
export { apiClient };

export default api;
